import React, { useCallback, useEffect, useState } from "react";
import { Modal, Spin, Tooltip, Input, DatePicker } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

import UserAvatar from "../../components/UserAvatar";
import * as worklogsApi from "../../../../core/data/redux/services/worklogsApi";
import { selectActiveCompany } from "../../../../core/data/redux/projects/selectors";
import { errMsg } from "../../helpers/errMsg.helper";
import { SETTINGS_MODAL_PROPS } from "../../helpers/modal.style";
import { notify } from "../../../../services/toast.config";

import type { Worklog } from "../../../../core/data/redux/projects/types";

interface WorklogPanelProps {
  issueId: number;
  initialWorklogs?: Worklog[];
  estimateSeconds?: number | null;
  remainingSeconds?: number | null;
  currentUserId: number | null;
}

const formatDuration = (seconds: number | null | undefined): string => {
  if (!seconds || seconds <= 0) return "0m";
  const parts: string[] = [];
  let rem = Math.round(seconds);
  const weeks = Math.floor(rem / (5 * 8 * 3600));
  if (weeks > 0) { parts.push(`${weeks}w`); rem -= weeks * 5 * 8 * 3600; }
  const days = Math.floor(rem / (8 * 3600));
  if (days > 0) { parts.push(`${days}d`); rem -= days * 8 * 3600; }
  const hours = Math.floor(rem / 3600);
  if (hours > 0) { parts.push(`${hours}h`); rem -= hours * 3600; }
  const minutes = Math.floor(rem / 60);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.join(" ") || "0m";
};

const parseDuration = (input: string): number => {
  const re = /(\d+(?:\.\d+)?)\s*(w|d|h|m)/gi;
  let total = 0;
  let match;
  while ((match = re.exec(input)) !== null) {
    const val = parseFloat(match[1]);
    switch (match[2].toLowerCase()) {
      case "w": total += val * 5 * 8 * 3600; break;
      case "d": total += val * 8 * 3600; break;
      case "h": total += val * 3600; break;
      case "m": total += val * 60; break;
    }
  }
  return Math.round(total);
};

const WorklogPanel: React.FC<WorklogPanelProps> = ({
  issueId,
  initialWorklogs = [],
  estimateSeconds,
  remainingSeconds,
  currentUserId,
}) => {
  const activeCompany = useSelector(selectActiveCompany);
  const [worklogs, setWorklogs] = useState<Worklog[]>(initialWorklogs);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [timeSpent, setTimeSpent] = useState("");
  const [description, setDescription] = useState("");
  const [startedAt, setStartedAt] = useState<dayjs.Dayjs | null>(dayjs());

  const reload = useCallback(async () => {
    if (!activeCompany?.company_id) return;
    setLoading(true);
    try {
      const data = await worklogsApi.listByIssue(
        activeCompany.company_id,
        activeCompany.company_type,
        issueId
      );
      setWorklogs(data?.worklogs || []);
    } catch {
      setWorklogs([]);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, issueId]);

  useEffect(() => {
    if (initialWorklogs.length === 0) reload();
  }, [reload, initialWorklogs.length]);

  const handleSubmit = async () => {
    const seconds = parseDuration(timeSpent);
    if (seconds <= 0) {
      notify.error("Enter time spent (e.g. 2d 4h 30m)");
      return;
    }
    setSubmitting(true);
    try {
      await worklogsApi.createWorklog(
        activeCompany.company_id,
        activeCompany.company_type,
        {
          issue_id: issueId,
          seconds_spent: seconds,
          started_at: startedAt?.toISOString(),
          description: description || null,
        }
      );
      notify.success("Work logged");
      setTimeSpent("");
      setDescription("");
      setStartedAt(dayjs());
      setModalOpen(false);
      await reload();
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to log work"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await worklogsApi.deleteWorklog(
        activeCompany.company_id,
        activeCompany.company_type,
        id
      );
      notify.success("Worklog deleted");
      await reload();
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to delete worklog"));
    }
  };

  const totalSpent = worklogs.reduce((sum, w) => sum + (w.seconds_spent || 0), 0);
  const estimateTotal = estimateSeconds || 0;
  const pct = estimateTotal > 0 ? Math.min(100, Math.round((totalSpent / estimateTotal) * 100)) : 0;
  const overBudget = totalSpent > estimateTotal && estimateTotal > 0;

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center gap-2">
          <span className="small text-muted">Logged: <strong>{formatDuration(totalSpent)}</strong></span>
          {estimateTotal > 0 && (
            <span className="small text-muted">
              / Est: <strong>{formatDuration(estimateTotal)}</strong>
              {remainingSeconds !== null && remainingSeconds !== undefined && (
                <> — Rem: <strong>{formatDuration(remainingSeconds)}</strong></>
              )}
            </span>
          )}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-info"
          style={{ borderRadius: 6 }}
          onClick={() => setModalOpen(true)}
        >
          <i className="ti ti-clock-plus me-1" />Log Work
        </button>
      </div>

      {estimateTotal > 0 && (
        <div className="progress mb-3" style={{ height: 6, borderRadius: 3 }}>
          <div
            className={`progress-bar ${overBudget ? "bg-danger" : "bg-success"}`}
            style={{ width: `${pct}%`, transition: "width 0.3s" }}
          />
        </div>
      )}

      {loading && worklogs.length === 0 ? (
        <div className="text-center py-2"><Spin size="small" /></div>
      ) : worklogs.length === 0 ? (
        <p className="text-muted small mb-0">No work logged yet.</p>
      ) : (
        <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
          {worklogs.map((w) => (
            <li
              key={w.id}
              className="d-flex align-items-start gap-2 p-2"
              style={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
            >
              <UserAvatar
                name={w.user_snapshot?.name}
                img={w.user_snapshot?.img}
                size={28}
                fontSize={11}
              />
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2">
                  <strong>{formatDuration(w.seconds_spent)}</strong>
                  <span className="text-muted">by {w.user_snapshot?.name || `User #${w.user_id}`}</span>
                  <span className="text-muted">{dayjs(w.started_at).format("MMM D, YYYY")}</span>
                </div>
                {w.description && <div className="text-muted mt-1">{w.description}</div>}
              </div>
              {currentUserId === w.user_id && (
                <Tooltip title="Delete">
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    style={{ borderRadius: 6 }}
                    onClick={() => handleDelete(w.id)}
                  >
                    <i className="ti ti-trash" style={{ color: "#ef4444" }} />
                  </button>
                </Tooltip>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal
        {...SETTINGS_MODAL_PROPS}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: "#3b82f618",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#3b82f6",
                fontSize: 15,
              }}
            >
              <ClockCircleOutlined />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Log Work</div>
              <div
                style={{
                  fontSize: 11.5,
                  color: "#94a3b8",
                  fontWeight: 400,
                }}
              >
                Record time spent on this issue
              </div>
            </div>
          </div>
        }
        footer={
          <div
            style={{
              padding: "14px 28px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <button
              className="btn btn-light border px-4"
              style={{ borderRadius: 8 }}
              onClick={() => setModalOpen(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              className="btn px-4"
              style={{
                borderRadius: 8,
                background: "#10b981",
                color: "#fff",
                fontWeight: 600,
              }}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Logging...
                </>
              ) : (
                <>
                  <ClockCircleOutlined className="me-2" />
                  Log
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="mb-3">
          <label className="form-label small fw-semibold">Time Spent</label>
          <Input
            placeholder="e.g. 2d 4h 30m"
            value={timeSpent}
            onChange={(e) => setTimeSpent(e.target.value)}
          />
          <div className="text-muted small mt-1">w = weeks, d = days (8h), h = hours, m = minutes</div>
        </div>
        <div className="mb-3">
          <label className="form-label small fw-semibold">Started At</label>
          <DatePicker
            className="w-100"
            value={startedAt}
            onChange={(v) => setStartedAt(v)}
            showTime
          />
        </div>
        <div className="mb-3">
          <label className="form-label small fw-semibold">Description</label>
          <Input.TextArea
            rows={2}
            placeholder="What did you work on?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </Modal>
    </>
  );
};

export default WorklogPanel;
